import React from 'react';
import { Clock, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { fmtQ } from '@/components/po/PoLinePicker';

const SOURCE = { greige_sort: 'Sort No Assignment', fob_tag: 'USN Tagging', section: 'Purchase Receipt' };
const isAdmin = () => { try { return ['Admin', 'Super Admin'].includes(JSON.parse(localStorage.getItem('user') || '{}').role); } catch { return false; } };

const Bundles = ({ r }) => {
  const list = r.bundle_barcodes?.length ? r.bundle_barcodes : (r.bundle_barcode ? [r.bundle_barcode] : []);
  if (!list.length) return <span className="text-gray-400">—</span>;
  return <span className="font-mono text-[11px]" title={list.join(', ')}>{list.slice(0, 2).join(', ')}{list.length > 2 ? ` +${list.length - 2}` : ''}</span>;
};

/** Where every receipt on this PO came from — section, delivery, bundles, sort no — with an Admin 'reverse' for corrections. */
export const ReceiptHistory = ({ receipts, items, onChanged }) => {
  const reverse = async (r) => {
    const reason = window.prompt(`Reverse ${fmtQ(r.received_qty)} ${r.unit || ''} on ${r.supplier_design_name || 'this line'}? Reason:`);
    if (reason === null) return;
    try {
      await apiClient.post(`/po-receipts/${r.receipt_id}/reverse`, { reason });
      toast.success('Receipt reversed — the line balance is back');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reverse'); }
  };
  if (!receipts.length) {
    return <Card><CardContent className="py-6 text-center text-sm text-gray-500" data-testid="po-no-receipts">Nothing received yet — quantities are counted from the section screens.</CardContent></Card>;
  }
  return (
    <Card data-testid="po-receipt-history">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" />Receipt History ({receipts.filter(r => r.status !== 'REVERSED').length})</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Section</TableHead><TableHead>Delivery</TableHead><TableHead>Bundles</TableHead>
              <TableHead>Item / Sort No</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Received By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {receipts.map((r) => {
                const item = items.find((i) => i.po_item_id === r.po_item_id);
                const off = r.status === 'REVERSED';
                return (
                  <TableRow key={r.receipt_id} className={off ? 'opacity-50' : ''} data-testid={`po-receipt-${r.receipt_id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(r.received_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{r.section || '—'}<div className="text-[10px] text-gray-400">{SOURCE[r.source] || 'Typed on PO page'}</div></TableCell>
                    <TableCell className="text-xs font-mono">{r.document_barcode || r.linked_entry?.entry_id || '—'}{r.entry_id && r.document_barcode && <div className="text-[10px] text-gray-400">{r.entry_id}</div>}</TableCell>
                    <TableCell><Bundles r={r} /></TableCell>
                    <TableCell className="text-sm">{item?.supplier_design_name || r.supplier_design_name || r.po_item_id}{r.sort_no && <div className="text-[11px] font-mono text-purple-700">{r.sort_no}</div>}</TableCell>
                    <TableCell className={`text-right tabular-nums ${off ? 'line-through' : 'font-semibold'}`}>{fmtQ(r.received_qty)} {r.unit || item?.unit}</TableCell>
                    <TableCell className="text-xs">{r.received_by_name}{r.remarks && <div className="text-[10px] text-gray-500">{r.remarks}</div>}</TableCell>
                    <TableCell className="text-right">
                      {off ? <Badge variant="outline" className="text-[10px]" title={`${r.reversed_by || ''} · ${r.reverse_reason || ''}`}>Reversed</Badge>
                        : r.grn_locked ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-[10px] text-emerald-800" title="Final GRN — an Admin must reopen it before this receipt can be reversed" data-testid={`po-receipt-grn-${r.receipt_id}`}>On {r.grn_number}</Badge>
                        : isAdmin() && <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => reverse(r)} data-testid={`po-receipt-reverse-${r.receipt_id}`}><Undo2 className="w-3.5 h-3.5 mr-1" />Reverse</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
